/**
 * Camera: where on the board the output frame looks at time t.
 *
 * Three sources, merged into one keyframe list per project version:
 * - scene frames: without other keys the camera sits on the current scene's frame; a "pan"
 *   transition travels there from the previous scene's frame;
 * - auto-camera (project.camera.auto): frames each element while it draws, then pulls back;
 * - manual keyframes. A manual key whose id equals an auto key's id overrides that key, so any
 *   auto key can be adjusted individually.
 */

import { chain, ease, lerp, rotate, scale, translate, type Easing, type Mat } from "./math";
import type { CameraKey, Project, Rect, Scene } from "./project.gen";
import { sceneIndexAt, sortedScenes, type ElementTimes } from "./timeline";

export interface CameraState {
  /** view centre, board units */
  x: number;
  y: number;
  /** 1 = output shows `output.width` board units across */
  zoom: number;
  /** degrees, clockwise */
  rotation: number;
}

export type CameraFn = (t: number) => CameraState;

export function frameRect(project: Project, scene: Scene | undefined): Rect {
  return scene?.frame ?? { x: 0, y: 0, w: project.output.width, h: project.output.height };
}

export function cameraForRect(project: Project, r: Rect): CameraState {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2, zoom: project.output.width / r.w, rotation: 0 };
}

/** The board rectangle the camera shows. */
export function viewRect(project: Project, c: CameraState): Rect {
  const w = project.output.width / c.zoom;
  const h = project.output.height / c.zoom;
  return { x: c.x - w / 2, y: c.y - h / 2, w, h };
}

export function interpolate(a: CameraState, b: CameraState, u: number): CameraState {
  // zoom interpolates geometrically so zooming feels even
  return {
    x: lerp(a.x, b.x, u),
    y: lerp(a.y, b.y, u),
    zoom: Math.exp(lerp(Math.log(a.zoom), Math.log(b.zoom), u)),
    rotation: lerp(a.rotation, b.rotation, u),
  };
}

const st = (k: CameraKey): CameraState => ({ x: k.x, y: k.y, zoom: k.zoom, rotation: k.rotation });

/** Keyframe interpolation. The easing of the *later* key shapes the move into it. */
export function keyframesAt(keys: CameraKey[], t: number): CameraState | null {
  if (keys.length === 0) return null;
  const ks = isSorted(keys) ? keys : [...keys].sort((a, b) => a.t - b.t);
  if (t <= ks[0].t) return st(ks[0]);
  const last = ks[ks.length - 1];
  if (t >= last.t) return st(last);
  let lo = 0;
  let hi = ks.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (ks[mid].t <= t) lo = mid;
    else hi = mid;
  }
  const a = ks[lo];
  const b = ks[hi];
  const u = b.t > a.t ? (t - a.t) / (b.t - a.t) : 1;
  return interpolate(st(a), st(b), ease(b.easing as Easing, u));
}

function isSorted(keys: CameraKey[]): boolean {
  for (let i = 1; i < keys.length; i++) if (keys[i].t < keys[i - 1].t) return false;
  return true;
}

/** Camera from scene frames only (no keyframes). */
export function sceneCameraAt(project: Project, t: number): CameraState {
  const scenes = sortedScenes(project);
  if (scenes.length === 0) return cameraForRect(project, frameRect(project, undefined));
  const i = sceneIndexAt(scenes, t);
  const s = scenes[i];
  const here = cameraForRect(project, frameRect(project, s));
  if (i > 0 && s.transition.type === "pan" && s.transition.duration > 0 && t < s.start + s.transition.duration) {
    const from = cameraForRect(project, frameRect(project, scenes[i - 1]));
    const u = Math.max(0, (t - s.start) / s.transition.duration);
    return interpolate(from, here, ease("easeInOut", u));
  }
  return here;
}

/**
 * The keyframe list in effect: manual keys, plus (with auto-camera) auto keys not overridden by a
 * manual key of the same id. Empty = follow the scene frames.
 */
export function resolveKeys(project: Project, auto: CameraKey[]): CameraKey[] {
  const manual = project.camera.keyframes;
  if (!project.camera.auto) return [...manual].sort((a, b) => a.t - b.t);
  const overridden = new Set(manual.map((k) => k.id));
  return [...auto.filter((k) => !overridden.has(k.id)), ...manual].sort((a, b) => a.t - b.t);
}

export function makeCamera(project: Project, keys: CameraKey[]): CameraFn {
  if (keys.length === 0) return (t) => sceneCameraAt(project, t);
  return (t) => keyframesAt(keys, t)!;
}

/** Convenience for callers without element bounds: manual keys or scene frames. */
export function cameraAt(project: Project, t: number): CameraState {
  return makeCamera(project, resolveKeys({ ...project, camera: { ...project.camera, auto: false } }, []))(t);
}

/**
 * Auto-camera keys. For each scene: start on the scene frame (panning in if the scene pans);
 * before each drawn element, move (0.6 s) to frame it with padding — zoom between 1 and autoMaxZoom,
 * staying inside the scene frame — hold while it draws; pull back to the full frame after the
 * scene's last element, or whenever there is a pause longer than 2 s.
 */
export function autoCameraKeys(project: Project, times: ReadonlyMap<string, ElementTimes>, bounds: ReadonlyMap<string, Rect>): CameraKey[] {
  const scenes = sortedScenes(project);
  const keys: CameraKey[] = [];
  const pad = project.camera.autoPadding;
  const W = project.output.width;
  const H = project.output.height;
  const push = (id: string, t: number, c: CameraState, easing: CameraKey["easing"]) => {
    const last = keys[keys.length - 1];
    if (last && t <= last.t + 0.02) {
      if (t < last.t) return;
      keys.pop(); // same instant: the later decision wins
    }
    keys.push({ id, t: round3(t), x: round1(c.x), y: round1(c.y), zoom: round3(c.zoom), rotation: 0, easing, source: "auto" });
  };

  scenes.forEach((s, si) => {
    const fr = frameRect(project, s);
    const full = cameraForRect(project, fr);
    const end = si + 1 < scenes.length ? scenes[si + 1].start : Infinity;
    if (si > 0 && s.transition.type === "pan" && s.transition.duration > 0) {
      push(`auto-${s.id}-from`, s.start, cameraForRect(project, frameRect(project, scenes[si - 1])), "hold");
      push(`auto-${s.id}-in`, s.start + s.transition.duration, full, "easeInOut");
    } else push(`auto-${s.id}-in`, s.start, full, "hold");

    const els = project.elements
      .filter((e) => e.sceneId === s.id && !e.hidden && e.enter.mode !== "instant" && bounds.has(e.id))
      .sort((a, b) => times.get(a.id)!.enter0 - times.get(b.id)!.enter0);
    let cur = full;
    let lastEnd = s.start + (s.transition.type === "pan" ? s.transition.duration : 0);
    for (const e of els) {
      const tm = times.get(e.id)!;
      if (tm.enter0 >= end) break;
      const b = bounds.get(e.id)!;
      const zoom = clamp(Math.min(W / (b.w * (1 + 2 * pad)), H / (b.h * (1 + 2 * pad))), full.zoom, full.zoom * Math.max(1, project.camera.autoMaxZoom));
      const vw = W / zoom;
      const vh = H / zoom;
      const target: CameraState = {
        x: clamp(b.x + b.w / 2, fr.x + vw / 2, fr.x + fr.w - vw / 2),
        y: clamp(b.y + b.h / 2, fr.y + vh / 2, fr.y + fr.h - vh / 2),
        zoom,
        rotation: 0,
      };
      if (tm.enter0 - lastEnd > 2) {
        push(`auto-${e.id}-rest`, lastEnd + 0.3, cur, "hold");
        push(`auto-${e.id}-back`, lastEnd + 1.1, full, "easeInOut");
        cur = full;
      }
      const t0 = Math.max(tm.enter0 - 0.6, (keys[keys.length - 1]?.t ?? s.start) + 0.05);
      push(`auto-${e.id}-from`, t0, cur, "hold");
      push(`auto-${e.id}`, Math.max(tm.enter0, t0 + 0.2), target, "easeInOut");
      push(`auto-${e.id}-hold`, Math.max(tm.enter1, t0 + 0.25), target, "linear");
      cur = target;
      lastEnd = tm.enter1;
    }
    // pull back to the full frame after the last element, if the scene runs on long enough
    if (els.length > 0 && lastEnd + 1.2 < end) push(`auto-${s.id}-out`, lastEnd + 1.1, full, "easeInOut");
  });
  return keys;
}

const clamp = (v: number, lo: number, hi: number) => (hi < lo ? (lo + hi) / 2 : v < lo ? lo : v > hi ? hi : v);
const round1 = (v: number) => Math.round(v * 10) / 10;
const round3 = (v: number) => Math.round(v * 1000) / 1000;

/** Board → output-pixel matrix for a camera state and an output of outW × outH pixels. */
export function cameraMatrix(project: Project, cam: CameraState, outW: number, outH: number): Mat {
  const px = outW / project.output.width; // render resolution relative to the project's base width
  return chain(translate(outW / 2, outH / 2), scale(px * cam.zoom), rotate(-cam.rotation), translate(-cam.x, -cam.y));
}
